import { Done, Language } from '@coconut-software/ui/dist/icons';
import clsx from 'clsx';
import type { ChangeEvent, FC } from 'react';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles, useTheme } from 'react-jss';
import { useHistory } from 'react-router';
import { LOCALE_NAMES } from '../../shared/constants';
import { LocaleContext } from '../contexts/LocaleContext';
import { SelectionContext } from '../contexts/SelectionContext';
import { SettingsContext } from '../contexts/SettingsContext';
import Languages from '../helpers/Languages';
import { URLtoCleanString } from '../helpers/Url';

const useStyles = createUseStyles((theme: any) => ({
  radio: {
    '&:focus + span': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.contrastText}`,
    },
    '&:checked + span, &:active + span': {
      background: 'var(--color-primary-main)',
    },
  },
  label: {
    appearance: 'button',
    '& svg': {
      width: '1.5rem',
      height: '1.5rem',
    },
  },
  variantSolid: {
    '& + span': {
      background: 'var(--color-primary-darker)',
    },
    '&:checked + span, &:active + span': {
      background: 'var(--color-primary-dark)',
    },
  },
}));

export enum Orders {
  Row,
  Column,
}

type Order = {
  [key in Orders]: string;
};

const orders: Order = {
  [Orders.Row]: 'flex flex-row gap-x-4',
  [Orders.Column]: 'flex flex-col gap-y-4 items-center',
};

type Variant = 'default' | 'solid';

interface EnhancedKioskLanguageRadioGroup {
  displayIcon?: boolean;
  onChange?: () => void;
  order: Orders;
  variant?: Variant;
}

const EnhancedKioskLanguageRadioGroup: FC<EnhancedKioskLanguageRadioGroup> = ({
  displayIcon,
  onChange,
  order,
  variant = 'default',
}) => {
  const classes = useStyles({ theme: useTheme() });
  const intl = useIntl();
  const [locale, setLocale] = useContext(LocaleContext);
  const { availableLanguages } = useContext(SettingsContext);
  const history = useHistory();
  const [, setSelections] = useContext(SelectionContext);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget.checked) {
      return;
    }

    const abbr = event.currentTarget.value;

    Languages.setCurrent(abbr);
    setLocale(abbr);

    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set('lang', abbr);
    history.push({ search: URLtoCleanString(searchParams) });
    setSelections({ language: abbr, search: URLtoCleanString(searchParams) });

    onChange?.();
  };

  return (
    <fieldset
      aria-label={intl.formatMessage({
        id: 'EnhancedKioskLanguagePicker.select_language',
      })}
      className={orders[order]}
      role="radiogroup"
    >
      {availableLanguages.map((language) => (
        <label className="w-full" key={language.abbr}>
          <input
            checked={locale === language.abbr}
            className={clsx(
              'sr-only',
              classes.radio,
              variant === 'solid' && classes.variantSolid,
            )}
            name={`language-${language.abbr}`}
            onChange={handleChange}
            type="radio"
            value={language.abbr}
          />
          <span
            className={clsx(
              'text-xl sm:text-2xl font-semibold px-8 py-4 rounded-full flex flex-row justify-center items-center gap-x-4 whitespace-no-wrap cursor-pointer',
              classes.label,
            )}
          >
            {displayIcon ? (
              locale === language.abbr ? (
                <Done />
              ) : (
                <Language />
              )
            ) : null}
            {LOCALE_NAMES[language.abbr]}
          </span>
        </label>
      ))}
    </fieldset>
  );
};

export { EnhancedKioskLanguageRadioGroup };
